import { getFreshToken } from '../auth';
import { APP_CONFIG } from '../../config/app';
import { createBrainFrameState, flushBrainFrames, pushBrainFrames } from './brainFrames';
import { getActiveWorkspaceId, PERSONAL_WORKSPACE_ID } from './workspaceContext';

export function resolveBrainWorkspaceId(pinnedWorkspaceId) {
  const raw =
    pinnedWorkspaceId !== undefined ? pinnedWorkspaceId : getActiveWorkspaceId();
  if (!raw || raw === PERSONAL_WORKSPACE_ID) return '';
  return raw;
}

async function readError(response) {
  const text = await response.text();
  if (!text) return `Brain request failed (HTTP ${response.status}).`;
  try {
    const data = JSON.parse(text);
    return data.error || data.message || text;
  } catch {
    return text;
  }
}

/**
 * POST to the brain Function URL and stream the reply as plain text.
 * onChunk receives the full text so far after each chunk.
 */
const DOOR_TEST_LINE =
  'Door is open. This is a fixed test line — the model is not here yet.';

function messageContent(m) {
  if (m.role === 'assistant' && m.ask) {
    return [m.text, m.ask].filter(Boolean).join('\n');
  }
  return m.text;
}

export function conversationForSend(messages, nextUserText) {
  const prior = (messages || [])
    .filter(
      (m) =>
        (m.role === 'user' || m.role === 'assistant') &&
        (m.text || m.ask) &&
        m.text !== DOOR_TEST_LINE &&
        !m.error &&
        !m.pending
    )
    .map((m) => ({ role: m.role, content: messageContent(m) }));
  const text = String(nextUserText || '').trim();
  if (text) prior.push({ role: 'user', content: text });
  return prior;
}

async function brainPost(workspaceId, body, signal) {
  const url = APP_CONFIG.brainUrl;
  if (!url) throw new Error('Brain is not connected yet.');
  const wsId = resolveBrainWorkspaceId(workspaceId);
  if (!wsId) throw new Error('This request is missing the brain.');
  const token = await getFreshToken();
  return fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      'X-Workspace-Id': wsId,
    },
    body: JSON.stringify(body),
    signal,
  });
}

export async function confirmBrainApply(workspaceId, sentence, signal) {
  const text = String(sentence || '').trim();
  if (!text) return { ok: true };
  const response = await brainPost(workspaceId, { action: 'graph.diary', text }, signal);
  if (!response.ok) throw new Error(await readError(response));
  return response.json();
}

export async function fetchMemorySnapshot(workspaceId, signal) {
  const response = await brainPost(workspaceId, { action: 'memory.snapshot' }, signal);
  if (!response.ok) throw new Error(await readError(response));
  return response.json();
}

export async function streamBrainReply({
  text,
  messages,
  workspaceId,
  campaignId,
  onChunk,
  onChip,
  onAsk,
  onUndo,
  onPreview,
  signal,
} = {}) {
  const response = await brainPost(
    workspaceId,
    {
      text: text || '',
      messages: messages || conversationForSend([], text),
      campaignId: campaignId || undefined,
    },
    signal
  );

  if (!response.ok) {
    throw new Error(await readError(response));
  }

  const frames = createBrainFrameState({ onChunk, onChip, onAsk, onUndo, onPreview });
  if (!response.body) {
    pushBrainFrames(frames, await response.text());
    return flushBrainFrames(frames);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    pushBrainFrames(frames, decoder.decode(value, { stream: true }));
  }
  pushBrainFrames(frames, decoder.decode());
  return flushBrainFrames(frames);
}
